import { useMemo } from 'react';
import { NormalizeFullParams } from '../constants/fullShipment';
import { DAYS_WINDOW_DEFAULT } from '../constants/periods';
import { AddDays, DaysBetween, ToIsoDate } from '../utils/dateUtils';
import { useOperacoesStore } from './Store';
import {
    AvgPeriod,
    BestDay,
    BuildInventoryGroups,
    CancelPercent,
    CancelledCount,
    Coverage,
    FullDemand,
    FullShipment,
    MonthVariation,
    OrderCount,
    PaidCount,
    PeriodSplit,
    SumColumnWindow,
    SumWindow,
    WeekGrowth,
} from './derive';

/**
 * Materializa séries fixas usadas por sparklines/charts:
 *   - dailyAll : 90 slots ending at anchor (idx 89 = anchor).
 *   - daily30  : últimos 30 slots (dailyAll.slice(-30)).
 *   - daily    : últimos 7 slots (dailyAll.slice(-7)).
 * Datas sem venda contam como 0.
 *
 * Mantém parity com o useOperacoesData legado que produzia esses 3 arrays —
 * Overview chart "Últimos 30 dias" lê daily30, Sparkline 7d lê daily.
 */
function BuildDailySeries(dailyByDate, anchorDateIso) {
    const dailyAll = new Array(DAYS_WINDOW_DEFAULT).fill(0);
    if (anchorDateIso) {
        const anchorDate = new Date(`${anchorDateIso}T00:00:00Z`);
        for (let i = 0; i < DAYS_WINDOW_DEFAULT; i += 1) {
            const day = AddDays(anchorDate, -(DAYS_WINDOW_DEFAULT - 1 - i));
            const iso = ToIsoDate(day);
            dailyAll[i] = Number(dailyByDate?.get(iso) || 0);
        }
    }
    return {
        dailyAll,
        daily30: dailyAll.slice(-30),
        daily: dailyAll.slice(-7),
    };
}

/**
 * Normaliza os campos de estoque vindos do backend `getStocksBatch` para o
 * shape esperado pelo DetailPanel:
 *   - bling     : total Bling (number)
 *   - mlAvail   : disponível no ML (mercadolivre.available_quantity)
 *   - mlUnavail : indisponível ML (mercadolivre.not_available_quantity)
 *   - mlTotal   : total ML (mercadolivre.total)
 *   - depots    : [{id, name, qty}] dedupados por warehouse
 *   - mercadolivre : objeto completo (StockBreakdownPanel usa not_available_detail)
 */
function ExtractStockFields(stock) {
    const ml = stock?.mercadolivre || {};
    const mlAvail = Number(ml.available_quantity ?? 0);
    const mlUnavail = Number(ml.not_available_quantity ?? 0);
    const mlTotal = Number(ml.total ?? mlAvail + mlUnavail);

    // depots: agrupa stock.warehouses por id e soma qty. Backend já filtra
    // somente entries BLING (sublocations distintas) — basta dedupar e somar.
    const depots = [];
    if (Array.isArray(stock?.warehouses)) {
        const byId = new Map();
        for (const w of stock.warehouses) {
            const idRaw = w?.id ?? w?.reference_id;
            const id = idRaw !== null && idRaw !== undefined ? String(idRaw) : '';
            if (!id) continue;
            const qty = Number(w?.quantity || 0);
            const existing = byId.get(id);
            if (existing) {
                existing.qty += qty;
            } else {
                byId.set(id, {
                    id,
                    name: w.name || w.descricao || null,
                    qty,
                });
            }
        }
        depots.push(...byId.values());
    }

    return {
        bling: Number(stock?.bling ?? stock?.quantity ?? 0),
        mlAvail,
        mlUnavail,
        mlTotal,
        depots,
        mercadolivre: stock?.mercadolivre || null,
        brand: stock?.brand || null,
        costPrice: stock?.costPrice !== null && stock?.costPrice !== undefined ? Number(stock.costPrice) : null,
    };
}

/**
 * Clipa um Map<isoDate, qty> ao período ativo. Renderer só recebe dates
 * dentro de [periodStartIso, periodEndIso] — sem dados fora.
 */
function ClipMapToPeriod(srcMap, periodStartIso, periodEndIso) {
    const out = new Map();
    if (!srcMap || !periodStartIso || !periodEndIso) return out;
    srcMap.forEach((value, iso) => {
        if (iso >= periodStartIso && iso <= periodEndIso) {
            out.set(iso, value);
        }
    });
    return out;
}

/**
 * Soma o breakdown de pedidos por status (`{paid, cancelled, other}`) dentro
 * do período ativo. Lê `orderTotalsByDate` do backend (distinct order ids
 * por status por dia) e produz totals zerados quando vazio.
 */
function SumOrderTotalsInPeriod(orderTotalsByDate, periodStartIso, periodEndIso) {
    const out = { paid: 0, cancelled: 0, other: 0 };
    if (!orderTotalsByDate || !periodStartIso || !periodEndIso) return out;
    orderTotalsByDate.forEach((bucket, iso) => {
        if (iso >= periodStartIso && iso <= periodEndIso) {
            out.paid += Number(bucket?.paid || 0);
            out.cancelled += Number(bucket?.cancelled || 0);
            out.other += Number(bucket?.other || 0);
        }
    });
    return out;
}

/**
 * Normaliza SKU pra comparação consistente (UPPERCASE trimmed).
 */
function NormSku(sku) {
    return String(sku || '')
        .trim()
        .toUpperCase();
}

/**
 * Constrói uma row pro modo SKU a partir do snapshot + viewConfig.
 * TODOS os campos derivados (avgPeriod, bestDay, sales*, week*, month*,
 * coverage) são calculados FRESH usando o ctx — nenhum cache no row.
 */
function BuildSkuRow({ sku, ctx, product, stock, sales, tags, mlbsBySku, titleBySku, tagsByMlb, mlListingsByMlb }) {
    // CLIPA ao período ativo — Renderer só vê dados do filtro. Sem leak:
    // dates fora de [periodStart, periodEnd] não aparecem em row.dailyByDate.
    const dailyByDate = ClipMapToPeriod(sales?.dailyByDate, ctx.periodStartIso, ctx.periodEndIso);
    const orderTotals = SumOrderTotalsInPeriod(sales?.orderTotalsByDate, ctx.periodStartIso, ctx.periodEndIso);
    const stockFields = ExtractStockFields(stock);
    const { bling, mlAvail, mlUnavail, mlTotal, depots, mercadolivre } = stockFields;
    const series = BuildDailySeries(dailyByDate, ctx.anchorDateIso);

    const consistentTitle =
        (product?.title && String(product.title).trim()) || titleBySku?.get(sku) || sales?.titleSample || sku;

    const periodTotal = SumWindow(dailyByDate, ctx.periodStartIso, ctx.periodEndIso);
    const bestDay = BestDay(dailyByDate, ctx);
    const avgPeriod = AvgPeriod(dailyByDate, ctx);
    const coverage = Coverage(dailyByDate, ctx, bling);
    // Envio Full — 2 etapas (ritmo de venda → cobertura/necessidade/tags).
    // Estoque do Full = mlTotal (regra do dono). Params do preset via ctx.
    const fullDemand = FullDemand(dailyByDate, ctx, ctx.fullParams);
    const full = FullShipment({ ...fullDemand, bling, mlTotal }, ctx.fullParams);

    // Tags = SKU-level (estoque/baixo/venda agregada) + UNION das tags dos
    // MLBs filhos (reputação + sem_venda_30d POR MLB). Backend grava as duas
    // camadas em tabelas separadas; aqui frontend só agrega pra display.
    // Sem spillover bugado: se um MLB não vendeu mas outro vendeu, o SKU NÃO
    // recebe "sem venda 30d" (só os MLBs específicos que não venderam).
    const skuAutoTags = Array.isArray(tags) ? tags.map(MapAutoTag) : [];
    const mlbList = mlbsBySku?.get(sku) || sales?.mlbs || [];
    const mlbUnionTags = new Set();
    for (const mlb of mlbList) {
        const payload = tagsByMlb?.get(mlb);
        if (payload?.tags) {
            for (const t of payload.tags) mlbUnionTags.add(MapAutoTag(t));
        }
    }
    // Merge final: SKU + UNION MLBs (dedup via Set).
    const autoTags = [...new Set([...skuAutoTags, ...mlbUnionTags])];
    const manualTags = Array.isArray(product?.tags) ? product.tags : [];

    // Anúncios linkados (pra popover "Ver anúncios" em SKU mode).
    const mlbListings = mlbList.map((mlb) => ({
        mlb,
        title: mlListingsByMlb?.get(mlb)?.title || null,
        permalink: mlListingsByMlb?.get(mlb)?.permalink || null,
        thumbnail: mlListingsByMlb?.get(mlb)?.thumbnail || null,
    }));
    // mlbTitle pra coluna individual — em SKU mode mostra título do primeiro
    // anúncio (visível em popover quando há +1).
    const firstMlb = mlbList[0] || null;
    const mlbTitle = firstMlb ? mlListingsByMlb?.get(firstMlb)?.title || null : null;
    // Foto: 1º anúncio que tem thumbnail (nem todo anúncio tem).
    const thumbnail = mlbListings.map((l) => l.thumbnail).find(Boolean) || null;

    return {
        sku,
        id: sku,
        anchorDateIso: ctx.anchorDateIso,
        mlb: sales?.mlbs?.[0] || product?.bling_id || null,
        mlbs: Array.isArray(sales?.mlbs) ? sales.mlbs : [],
        siblingMlbs: mlbsBySku?.get(sku) || sales?.mlbs || [],
        productId: product?.id || null,
        name: consistentTitle,
        // Título do anúncio ML (vem de ml_listings, atualizado pelo sync de
        // orders). Em SKU mode com +1 MLB mostra o do primeiro — popover
        // "Ver anúncios" expande com `mlbListings`.
        mlbTitle,
        mlbListings,
        thumbnail,
        brand: product?.brand || stock?.brand || null,
        supplier_code: product?.supplier_code || null,
        bling_id: product?.bling_id || null,
        units_per_pallet:
            product?.units_per_pallet !== null && product?.units_per_pallet !== undefined
                ? Number(product.units_per_pallet)
                : null,
        low_stock_threshold:
            product?.low_stock_threshold !== null && product?.low_stock_threshold !== undefined
                ? Number(product.low_stock_threshold)
                : 20,
        manualTags,
        tags: [...autoTags, ...manualTags],
        bling,
        depots,
        mlAvail,
        mlUnavail,
        mlTotal,
        mercadolivre,
        // Inventories do Full deste SKU (by_inventory). Primeiro = compat com
        // mercadolivre.inventory_id; a lista completa alimenta a coluna "+N".
        inventoryId: mercadolivre?.inventory_id || null,
        inventoryIds: InventoryIdsOf(mercadolivre),
        cost: Number(stockFields.costPrice || product?.cost_price || product?.cost || 0),
        price: Number(product?.price || product?.sale_price || 0),
        dailyByDate,
        // Séries pre-fatiadas pra sparkline + Overview "Últimos 8/30 dias".
        // Derivadas de dailyByDate JÁ CLIPADO — zero pra dias fora do período.
        dailyAll: series.dailyAll,
        daily: series.daily,
        daily30: series.daily30,
        // orderTotals computado em [periodStart, periodEnd] a partir do
        // breakdown diário do backend. Substitui o agregado snapshot-wide
        // antigo que ignorava o filtro.
        orderTotals,
        // orderCount = total de pedidos distintos no período (paid+cancelled+
        // other). Pré-computado aqui pra footer/sort/export/célula lerem o mesmo
        // número — valida 1:1 com paging.total de /orders/search?item=MLB.
        orderCount: orderTotals.paid + orderTotals.cancelled + orderTotals.other,
        // Valores agregados PRÉ-COMPUTADOS pelo Selector pra cada ViewConfig:
        // - periodTotal = soma do período exato
        // - avgPeriod   = média por dia no período
        // - bestDay     = { qty, dateIso } ou null
        // - sales*/week*/month* = janelas fixas relativas ao anchor
        // - coverage    = bling / avg7
        periodTotal,
        avgPeriod,
        avg7: SumColumnWindow(dailyByDate, ctx, 'sales7') / 7,
        best7: bestDay ? bestDay.qty : 0,
        bestDay: bestDay ? bestDay.qty : 0,
        bestDayDate: bestDay ? bestDay.dateIso : null,
        // Forecast 15d: regra de negócio (best7 * 7 + avg7 * 15). Pre-computado
        // aqui pra DetailPanel.Overview não recalcular em render.
        forecast: ComputeForecast({
            best7: bestDay ? bestDay.qty : 0,
            avg7: SumColumnWindow(dailyByDate, ctx, 'sales7') / 7,
        }),
        sales_today: Number(dailyByDate.get(ctx.anchorDateIso) || 0),
        sales_yday: Number(dailyByDate.get(MinusOneDayIso(ctx.anchorDateIso)) || 0),
        sales_d2: Number(dailyByDate.get(MinusNDaysIso(ctx.anchorDateIso, 3)) || 0),
        sales_d3: Number(dailyByDate.get(MinusNDaysIso(ctx.anchorDateIso, 4)) || 0),
        sales_d4: Number(dailyByDate.get(MinusNDaysIso(ctx.anchorDateIso, 5)) || 0),
        sales3: SumColumnWindow(dailyByDate, ctx, 'sales3'),
        sales5: SumColumnWindow(dailyByDate, ctx, 'sales5'),
        sales7: SumColumnWindow(dailyByDate, ctx, 'sales7'),
        sales30: SumColumnWindow(dailyByDate, ctx, 'sales30'),
        sales60: SumColumnWindow(dailyByDate, ctx, 'sales60'),
        sales90: SumColumnWindow(dailyByDate, ctx, 'sales90'),
        sales180: SumColumnWindow(dailyByDate, ctx, 'sales180'),
        sales365: SumColumnWindow(dailyByDate, ctx, 'sales365'),
        week1: SumColumnWindow(dailyByDate, ctx, 'week1'),
        week2: SumColumnWindow(dailyByDate, ctx, 'week2'),
        week3: SumColumnWindow(dailyByDate, ctx, 'week3'),
        week4: SumColumnWindow(dailyByDate, ctx, 'week4'),
        month1: SumColumnWindow(dailyByDate, ctx, 'month1'),
        month2: SumColumnWindow(dailyByDate, ctx, 'month2'),
        month3: SumColumnWindow(dailyByDate, ctx, 'month3'),
        coverage,
        // Envio Full — SKU-level nos 2 modos (ver contract.js / derive/FullShipment.js).
        fullAvgDay: fullDemand.fullAvgDay,
        fullHasHistory: fullDemand.fullHasHistory,
        fullCoverage: full.fullCoverage,
        fullNeed: full.fullNeed,
        fullSuggest: full.fullSuggest,
        // Coluna "Análise Full" — separada de row.tags (pedido do dono).
        fullTags: full.fullTags,
    };
}

/**
 * Camada FINAL que adiciona aos rows campos derivados de COMPARAÇÃO entre as
 * janelas já computadas. Separa pra não inflar BuildSkuRow/BuildMlbRow.
 */
function AttachComparisons(row, ctx) {
    row.weekGrowth = WeekGrowth(row);
    row.monthVariation = MonthVariation(row);
    row.periodSplit = PeriodSplit(row, ctx);
    row.orderCount = OrderCount(row);
    row.paidCount = PaidCount(row);
    row.cancelledCount = CancelledCount(row);
    row.cancelPercent = CancelPercent(row);
    return row;
}

function ComputeForecast({ best7, avg7 }) {
    return Math.round(Number(best7 || 0) * 7 + Number(avg7 || 0) * 15);
}

function MapAutoTag(t) {
    // Backend usa snake_case; UI exibe versões com espaço.
    const m = {
        sem_estoque: 'sem estoque',
        baixo_estoque: 'baixo estoque',
        sem_venda_30d: 'sem venda 30d',
        outbound: 'outbound',
        reputacao_warning: 'reputacao warning',
        reputacao_unhealthy: 'reputacao unhealthy',
    };
    return m[t] || t;
}

function MinusOneDayIso(iso) {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return ToIsoDate(d);
}

/**
 * Row pro modo MLB. Cada row = 1 anúncio (singleton em row.mlbs pra não
 * mostrar "+N" na coluna MLB). Lista completa de anúncios do SKU fica em
 * siblingMlbs (DetailPanel usa).
 */
function BuildMlbRow({
    mlb,
    ctx,
    sales,
    product,
    stock,
    mlbsBySku,
    titleBySku,
    tagsByMlb,
    mlListingsByMlb,
    skuTagsBySku,
    skuSales,
}) {
    const sku = NormSku(sales?.sku);
    // CLIPA ao período — mesmo princípio do BuildSkuRow.
    const dailyByDate = ClipMapToPeriod(sales?.dailyByDate, ctx.periodStartIso, ctx.periodEndIso);
    const stockFields = ExtractStockFields(stock);
    const { bling, mlAvail, mlUnavail, mlTotal, depots, mercadolivre } = stockFields;
    const series = BuildDailySeries(dailyByDate, ctx.anchorDateIso);

    const consistentTitle =
        (product?.title && String(product.title).trim()) || (sku && titleBySku?.get(sku)) || sales?.title || mlb;

    const bestDay = BestDay(dailyByDate, ctx);
    const avgPeriod = AvgPeriod(dailyByDate, ctx);
    const coverage = Coverage(dailyByDate, ctx, bling);
    // Envio Full é conceito de SKU: usa a série do SKU pai (soma dos anúncios
    // irmãos), não a do anúncio — senão cada MLB veria 1/N da demanda contra o
    // estoque inteiro do SKU e a cobertura sairia N× inflada.
    const skuDailyByDate = ClipMapToPeriod(skuSales?.dailyByDate, ctx.periodStartIso, ctx.periodEndIso);
    const fullDemand = FullDemand(skuDailyByDate, ctx, ctx.fullParams);
    const full = FullShipment({ ...fullDemand, bling, mlTotal }, ctx.fullParams);
    // Pedidos distintos do anúncio no período (backend agora rastreia por MLB,
    // espelho do SKU). Valida 1:1 com paging.total de /orders/search?item=MLB.
    const orderTotals = SumOrderTotalsInPeriod(sales?.orderTotalsByDate, ctx.periodStartIso, ctx.periodEndIso);

    // Tags do MLB = tags persistidas DESTE anúncio (inventory_mlb_tags) +
    // tags do SKU pai (estoque) que afetam todos os anúncios irmãos.
    // SEM spillover cross-MLB: cada MLB carrega só seus próprios sinais.
    const mlbPayload = tagsByMlb?.get(mlb);
    const mlbAutoTags = mlbPayload?.tags ? mlbPayload.tags.map(MapAutoTag) : [];
    const skuAutoTags = sku && skuTagsBySku?.has(sku) ? skuTagsBySku.get(sku).map(MapAutoTag) : [];
    const autoTags = [...new Set([...mlbAutoTags, ...skuAutoTags])];
    const manualTags = Array.isArray(product?.tags) ? product.tags : [];

    // Título do anúncio do ml_listings (mais atual que sales.title).
    const mlbListingEntry = mlListingsByMlb?.get(mlb);
    const mlbTitle = mlbListingEntry?.title || sales?.title || null;
    const mlbListings = [
        {
            mlb,
            title: mlbTitle,
            permalink: mlbListingEntry?.permalink || null,
            thumbnail: mlbListingEntry?.thumbnail || null,
        },
    ];
    const thumbnail = mlbListingEntry?.thumbnail || null;

    return {
        id: mlb,
        anchorDateIso: ctx.anchorDateIso,
        mlb,
        mlbs: [mlb],
        siblingMlbs: (sku && mlbsBySku?.get(sku)) || [mlb],
        sku: sku || null,
        productId: product?.id || null,
        name: consistentTitle,
        mlbTitle,
        mlbListings,
        thumbnail,
        brand: product?.brand || stock?.brand || null,
        supplier_code: product?.supplier_code || null,
        bling_id: product?.bling_id || null,
        units_per_pallet:
            product?.units_per_pallet !== null && product?.units_per_pallet !== undefined
                ? Number(product.units_per_pallet)
                : null,
        low_stock_threshold:
            product?.low_stock_threshold !== null && product?.low_stock_threshold !== undefined
                ? Number(product.low_stock_threshold)
                : 20,
        manualTags,
        tags: [...autoTags, ...manualTags],
        bling,
        depots,
        mlAvail,
        mlUnavail,
        mlTotal,
        mercadolivre,
        // Inventory DO anúncio (ml_listings) — não o agregado do SKU.
        inventoryId: mlbListingEntry?.inventoryId || null,
        inventoryIds: mlbListingEntry?.inventoryId ? [mlbListingEntry.inventoryId] : [],
        cost: Number(stockFields.costPrice || product?.cost_price || 0),
        price: Number(product?.price || 0),
        dailyByDate,
        dailyAll: series.dailyAll,
        daily: series.daily,
        daily30: series.daily30,
        // Pedidos distintos do anúncio no período — backend agora rastreia por
        // MLB (espelho do SKU); valida 1:1 com paging.total de item=MLB.
        orderTotals,
        orderCount: orderTotals.paid + orderTotals.cancelled + orderTotals.other,
        forecast: ComputeForecast({
            best7: bestDay ? bestDay.qty : 0,
            avg7: SumColumnWindow(dailyByDate, ctx, 'sales7') / 7,
        }),
        periodTotal: SumWindow(dailyByDate, ctx.periodStartIso, ctx.periodEndIso),
        avgPeriod,
        avg7: SumColumnWindow(dailyByDate, ctx, 'sales7') / 7,
        best7: bestDay ? bestDay.qty : 0,
        bestDay: bestDay ? bestDay.qty : 0,
        bestDayDate: bestDay ? bestDay.dateIso : null,
        sales_today: Number(dailyByDate.get(ctx.anchorDateIso) || 0),
        sales_yday: Number(dailyByDate.get(MinusOneDayIso(ctx.anchorDateIso)) || 0),
        sales_d2: Number(dailyByDate.get(MinusNDaysIso(ctx.anchorDateIso, 3)) || 0),
        sales_d3: Number(dailyByDate.get(MinusNDaysIso(ctx.anchorDateIso, 4)) || 0),
        sales_d4: Number(dailyByDate.get(MinusNDaysIso(ctx.anchorDateIso, 5)) || 0),
        sales3: SumColumnWindow(dailyByDate, ctx, 'sales3'),
        sales5: SumColumnWindow(dailyByDate, ctx, 'sales5'),
        sales7: SumColumnWindow(dailyByDate, ctx, 'sales7'),
        sales30: SumColumnWindow(dailyByDate, ctx, 'sales30'),
        sales60: SumColumnWindow(dailyByDate, ctx, 'sales60'),
        sales90: SumColumnWindow(dailyByDate, ctx, 'sales90'),
        sales180: SumColumnWindow(dailyByDate, ctx, 'sales180'),
        sales365: SumColumnWindow(dailyByDate, ctx, 'sales365'),
        week1: SumColumnWindow(dailyByDate, ctx, 'week1'),
        week2: SumColumnWindow(dailyByDate, ctx, 'week2'),
        week3: SumColumnWindow(dailyByDate, ctx, 'week3'),
        week4: SumColumnWindow(dailyByDate, ctx, 'week4'),
        month1: SumColumnWindow(dailyByDate, ctx, 'month1'),
        month2: SumColumnWindow(dailyByDate, ctx, 'month2'),
        month3: SumColumnWindow(dailyByDate, ctx, 'month3'),
        coverage,
        // Envio Full — SKU-level nos 2 modos (ver contract.js / derive/FullShipment.js).
        fullAvgDay: fullDemand.fullAvgDay,
        fullHasHistory: fullDemand.fullHasHistory,
        fullCoverage: full.fullCoverage,
        fullNeed: full.fullNeed,
        fullSuggest: full.fullSuggest,
        // Coluna "Análise Full" — separada de row.tags (pedido do dono).
        fullTags: full.fullTags,
    };
}

/** Lista de inventory_ids do agregado ML de um SKU (by_inventory → fallback inventory_id). */
function InventoryIdsOf(mercadolivre) {
    if (Array.isArray(mercadolivre?.by_inventory)) {
        const ids = mercadolivre.by_inventory.map((inv) => String(inv?.inventory_id || '').trim()).filter(Boolean);
        if (ids.length > 0) {
            return ids;
        }
    }
    return mercadolivre?.inventory_id ? [String(mercadolivre.inventory_id)] : [];
}

/**
 * Row pro modo INVENTORY (Full). 1 row = 1 inventory_id; anúncios que
 * compartilham o inventory têm as vendas SOMADAS e o estoque Full é o do
 * inventory (não o agregado do SKU). Reusa BuildSkuRow com entradas
 * sintéticas — o grupo vira um "SKU virtual":
 *   - sales : série somada dos anúncios membros (derive/InventoryGroups).
 *   - stock : Bling = SOMA dos SKUs membros (depósitos concatenados, o
 *             ExtractStockFields dedupa por id); ML = números do inventory.
 *   - product/tags: do SKU primário (mais vendido) + união das tags manuais
 *             e automáticas de todos os SKUs membros.
 * Assim TODOS os derivados (janelas, cobertura, Envio Full, comparativos)
 * saem da mesma implementação — nada recalculado à parte.
 *
 * ⚠️ Bling é por SKU, e um SKU pode ter N inventories: as N rows mostram o
 * MESMO Bling e a soma das sugestões pode passar do saldo real.
 */
function BuildInventoryRow({ group, ctx, productBySku, stockBySku, skuTagsBySku, tagsByMlb, mlListingsByMlb }) {
    const primarySku = group.primarySku || group.skus[0] || null;
    const product = primarySku ? productBySku.get(primarySku) : null;
    const memberStocks = group.skus.map((sku) => stockBySku?.[sku]).filter(Boolean);
    const stock = {
        bling: memberStocks.reduce((sum, s) => sum + Number(s?.bling ?? s?.quantity ?? 0), 0),
        warehouses: memberStocks.flatMap((s) => (Array.isArray(s?.warehouses) ? s.warehouses : [])),
        mercadolivre: group.mlStock || {
            inventory_id: group.inventoryId,
            total: 0,
            available_quantity: 0,
            not_available_quantity: 0,
            not_available_detail: [],
        },
        brand: memberStocks.find((s) => s?.brand)?.brand || null,
        costPrice: memberStocks.find((s) => s?.costPrice !== null && s?.costPrice !== undefined)?.costPrice ?? null,
    };
    const sales = {
        dailyByDate: group.dailyByDate,
        orderTotalsByDate: group.orderTotalsByDate,
        mlbs: group.mlbs,
        titleSample: group.titleSample,
    };
    const manualTags = [...new Set(group.skus.flatMap((sku) => productBySku.get(sku)?.tags || []))];
    const autoTags = [...new Set(group.skus.flatMap((sku) => skuTagsBySku?.get(sku) || []))];
    const productForRow = {
        ...(product || {}),
        tags: manualTags,
        title: product?.title || group.titleSample || group.inventoryId,
    };
    const row = BuildSkuRow({
        sku: primarySku || group.inventoryId,
        ctx,
        product: productForRow,
        stock,
        sales,
        tags: autoTags,
        // Sem índice SKU→MLBs: os anúncios do grupo vêm de sales.mlbs.
        mlbsBySku: null,
        titleBySku: null,
        tagsByMlb,
        mlListingsByMlb,
    });
    row.id = group.inventoryId;
    row.inventoryId = group.inventoryId;
    row.inventoryIds = [group.inventoryId];
    row.sku = primarySku;
    row.skus = group.skus;
    row.mlb = group.mlbs[0] || null;
    row.mlbs = group.mlbs;
    row.siblingMlbs = group.mlbs;
    return row;
}

/**
 * useOperacoesSelector: pega Store + ViewConfig e produz rows derivadas.
 * Reativo a mudanças no snapshot OU no viewConfig — sem cache externo.
 *
 * `viewConfig`:
 *   - periodStartIso, periodEndIso: range UTC ISO (já convertido pelo caller)
 *   - identifier: 'sku' | 'mlb'
 *   - brands, tags, search, stockRules: filtros
 */
export function useOperacoesSelector(viewConfig) {
    const { snapshot, loading, error, ensureRange, refetch } = useOperacoesStore();

    const ctx = useMemo(() => {
        if (!snapshot || !viewConfig?.periodStartIso || !viewConfig?.periodEndIso) return null;
        return {
            anchorDateIso: snapshot.anchorDateIso,
            periodStartIso: viewConfig.periodStartIso,
            periodEndIso: viewConfig.periodEndIso,
            periodDays: DaysBetween(viewConfig.periodStartIso, viewConfig.periodEndIso),
            asOf: snapshot.asOf,
            // Parâmetros do Envio Full (preset.full normalizado). Sempre completo.
            fullParams: NormalizeFullParams(viewConfig?.fullParams),
        };
    }, [snapshot, viewConfig?.periodStartIso, viewConfig?.periodEndIso, viewConfig?.fullParams]);

    // Garante range carregado >= periodDays.
    const periodDaysNeeded = ctx?.periodDays || 0;
    useMemo(() => {
        if (periodDaysNeeded > 0) ensureRange(periodDaysNeeded);
    }, [periodDaysNeeded, ensureRange]);

    // Cómputo único de AMBAS as views (SKU e MLB). DetailPanel precisa de
    // mlbRows com data completa MESMO em identifier='sku' (pra mostrar
    // breakdown por anúncio na aba Sales). `rows` é só uma view filtrada.
    const built = useMemo(() => {
        if (!snapshot || !ctx) return { allSkuRows: [], allMlbRows: [], allInventoryRows: [] };

        // Índices auxiliares — calculados 1x e reutilizados.
        const productBySku = new Map();
        for (const p of snapshot.products || []) {
            const sku = NormSku(p.sku);
            if (sku) productBySku.set(sku, p);
        }

        // Indices: MLBs por SKU + título canônico determinístico
        // (primeiro MLB ordenado alfabeticamente).
        const mlbsBySku = new Map();
        const titleBySku = new Map();
        snapshot.salesByMlb.forEach((entry, mlb) => {
            const sku = NormSku(entry.sku);
            if (!sku) return;
            if (!mlbsBySku.has(sku)) mlbsBySku.set(sku, []);
            mlbsBySku.get(sku).push(mlb);
            if (entry.title && !titleBySku.has(sku)) {
                titleBySku.set(sku, entry.title);
            }
        });
        // Adiciona MLBs do ml_listings (catálogo ML) que talvez não tenham
        // venda no período mas existem como anúncios ativos — garante que
        // SKUs sem venda recente ainda listem MLBs no popover.
        snapshot.mlListingsByMlb?.forEach?.((entry, mlb) => {
            const sku = NormSku(entry.sku);
            if (!sku) return;
            if (!mlbsBySku.has(sku)) mlbsBySku.set(sku, []);
            const list = mlbsBySku.get(sku);
            if (!list.includes(mlb)) list.push(mlb);
            if (entry.title && !titleBySku.has(sku)) titleBySku.set(sku, entry.title);
        });
        mlbsBySku.forEach((list) => {
            list.sort();
        });

        // Maps já hidratados pelo Store — passados pro builder consumir.
        const tagsByMlb = snapshot.tagsByMlb || new Map();
        const mlListingsByMlb = snapshot.mlListingsByMlb || new Map();
        const skuTagsBySku = snapshot.tagsBySku || new Map();

        // Sempre constrói rows MLB completas (com sales*/dailyByDate/etc) pra
        // que DetailPanel.Sales possa exibir breakdown e charts mesmo quando o
        // preset está em identifier='sku'.
        const allMlbRows = [];
        snapshot.salesByMlb.forEach((sales, mlb) => {
            const sku = NormSku(sales.sku);
            const product = sku ? productBySku.get(sku) : null;
            const stock = sku ? snapshot.stockBySku[sku] : null;
            const row = BuildMlbRow({
                mlb, ctx, sales, product, stock,
                mlbsBySku, titleBySku, tagsByMlb, mlListingsByMlb, skuTagsBySku,
                skuSales: sku ? snapshot.salesBySku.get(sku) || null : null,
            });
            AttachComparisons(row, ctx);
            allMlbRows.push(row);
        });

        // Modo SKU: union de catálogo + vendas + estoque.
        const allSkus = new Set([
            ...productBySku.keys(),
            ...snapshot.salesBySku.keys(),
            ...Object.keys(snapshot.stockBySku || {}),
        ]);
        const allSkuRows = [];
        allSkus.forEach((sku) => {
            const product = productBySku.get(sku);
            const stock = snapshot.stockBySku[sku];
            const sales = snapshot.salesBySku.get(sku);
            const tags = snapshot.tagsBySku.get(sku);
            const row = BuildSkuRow({
                sku, ctx, product, stock, sales, tags,
                mlbsBySku, titleBySku, tagsByMlb, mlListingsByMlb,
            });
            AttachComparisons(row, ctx);
            allSkuRows.push(row);
        });

        // Modo INVENTORY (Full): 1 row por inventory_id, vendas somadas dos
        // anúncios que compartilham o estoque. Barato (~centenas de grupos).
        const allInventoryRows = [];
        BuildInventoryGroups({
            mlListingsByMlb,
            salesByMlb: snapshot.salesByMlb,
            stockBySku: snapshot.stockBySku,
        }).forEach((group) => {
            const row = BuildInventoryRow({
                group, ctx, productBySku,
                stockBySku: snapshot.stockBySku, skuTagsBySku, tagsByMlb, mlListingsByMlb,
            });
            AttachComparisons(row, ctx);
            allInventoryRows.push(row);
        });

        return { allSkuRows, allMlbRows, allInventoryRows };
    }, [snapshot, ctx]);

    const identifier = viewConfig?.identifier || 'sku';
    const rows =
        identifier === 'mlb' ? built.allMlbRows : identifier === 'inventory' ? built.allInventoryRows : built.allSkuRows;

    const brandOpts = useMemo(() => {
        const set = new Set();
        for (const r of rows) if (r.brand) set.add(r.brand);
        return [...set].sort();
    }, [rows]);

    const tagOpts = useMemo(() => {
        const set = new Set();
        for (const r of rows) {
            // Tags gerais + Análise Full: o filtro de tags enxerga as duas colunas.
            for (const list of [r.tags, r.fullTags]) {
                if (!Array.isArray(list)) continue;
                for (const t of list) {
                    if (t) set.add(t);
                }
            }
        }
        return [...set].sort();
    }, [rows]);

    return {
        rows,
        allSkuRows: built.allSkuRows,
        allMlbRows: built.allMlbRows,
        allInventoryRows: built.allInventoryRows,
        ctx,
        loading,
        error,
        refetch,
        snapshot,
        brandOpts,
        tagOpts,
        ordersCount: snapshot?.ordersCount || 0,
        truncated: Boolean(snapshot?.truncated),
    };
}

function MinusNDaysIso(iso, n) {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - (n - 1));
    return ToIsoDate(d);
}
